import React, { useState, useRef, useCallback, useEffect } from 'react';
import { getMediaUrl } from '../services/api';
import './GalleryGridEditor.css';

function getDefaultConfig(photoCount) {
  if (photoCount <= 1) return { columns: 1, columnWidths: [100], rowHeight: 400, gap: 0 };
  if (photoCount === 2) return { columns: 2, columnWidths: [50, 50], rowHeight: 350, gap: 8 };
  if (photoCount <= 4) return { columns: 2, columnWidths: [50, 50], rowHeight: 280, gap: 8 };
  return { columns: 3, columnWidths: [33.33, 33.33, 33.34], rowHeight: 280, gap: 8 };
}

// Map theme to template-specific styles
const themeStyles = {
  bold: { borderRadius: 0, gap: 8, hasOverlay: true, overlayStyle: 'full' },
  clean: { borderRadius: 0, gap: 4, hasOverlay: false },
  creative: { borderRadius: 12, gap: 16, hasOverlay: true, overlayStyle: 'full' },
  pop: { borderRadius: 0, gap: 4, hasOverlay: true, overlayStyle: 'gradient' },
};

function GalleryGridEditor({ photos, config, onChange, onReorder, onCropPhoto, theme }) {
  const [dragSourceIndex, setDragSourceIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [resizingHandle, setResizingHandle] = useState(null);
  const gridRef = useRef(null);
  const startXRef = useRef(null);
  const startWidthsRef = useRef(null);

  const effectiveConfig = config || getDefaultConfig(photos.length);
  const { columns, columnWidths, rowHeight, gap } = effectiveConfig;
  const ts = themeStyles[theme] || themeStyles.bold;

  // Column count change
  const setColumnCount = (count) => {
    const widths = Array(count).fill(parseFloat((100 / count).toFixed(2)));
    // Ensure they sum to 100
    widths[widths.length - 1] = parseFloat(
      (100 - widths.slice(0, -1).reduce((s, w) => s + w, 0)).toFixed(2)
    );
    onChange({ ...effectiveConfig, columns: count, columnWidths: widths });
  };

  // Row height change
  const setRowHeight = (h) => {
    onChange({ ...effectiveConfig, rowHeight: h });
  };

  // Gap change
  const setGap = (g) => {
    onChange({ ...effectiveConfig, gap: g });
  };

  // Reset to defaults
  const handleReset = () => {
    onChange(null);
  };

  // Handle resize drag
  const handleResizeStart = useCallback(
    (e, handleIndex) => {
      e.preventDefault();
      setResizingHandle(handleIndex);
      startXRef.current = e.clientX;
      startWidthsRef.current = [...columnWidths];
    },
    [columnWidths]
  );

  const handleResizeMove = useCallback(
    (e) => {
      if (resizingHandle === null || !gridRef.current) return;
      const gridRect = gridRef.current.getBoundingClientRect();
      const gridWidth = gridRect.width;
      const deltaX = e.clientX - startXRef.current;
      const deltaPercent = (deltaX / gridWidth) * 100;

      const newWidths = [...startWidthsRef.current];
      const leftCol = resizingHandle;
      const rightCol = resizingHandle + 1;

      let newLeft = newWidths[leftCol] + deltaPercent;
      let newRight = newWidths[rightCol] - deltaPercent;

      // Enforce minimum 15%
      if (newLeft < 15) {
        newRight = newRight - (15 - newLeft);
        newLeft = 15;
      }
      if (newRight < 15) {
        newLeft = newLeft - (15 - newRight);
        newRight = 15;
      }

      if (newLeft >= 15 && newRight >= 15) {
        newWidths[leftCol] = parseFloat(newLeft.toFixed(2));
        newWidths[rightCol] = parseFloat(newRight.toFixed(2));
        onChange({ ...effectiveConfig, columnWidths: newWidths });
      }
    },
    [resizingHandle, effectiveConfig, onChange]
  );

  const handleResizeEnd = useCallback(() => {
    setResizingHandle(null);
    startXRef.current = null;
    startWidthsRef.current = null;
  }, []);

  useEffect(() => {
    if (resizingHandle !== null) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingHandle, handleResizeMove, handleResizeEnd]);

  // Photo drag-and-drop
  const handleDragStart = (e, index) => {
    setDragSourceIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (dragSourceIndex === null || dragSourceIndex === targetIndex) return;

    // Reorder: swap the two photos
    const reorderedPhotos = [...photos];
    const [moved] = reorderedPhotos.splice(dragSourceIndex, 1);
    reorderedPhotos.splice(targetIndex, 0, moved);

    // Build sort_order updates
    const updates = reorderedPhotos.map((photo, i) => ({
      id: photo.id,
      sort_order: i,
    }));

    onReorder(updates);
    setDragSourceIndex(null);
  };

  const handleDragEnd = () => {
    setDragSourceIndex(null);
    setDragOverIndex(null);
  };

  const gridTemplateColumns = columnWidths.map((w) => `${w}%`).join(' ');

  if (photos.length === 0) {
    return (
      <div className="gallery-grid-editor">
        <div className="gallery-grid-empty">
          No gallery photos yet. Add photos above to configure the grid layout.
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-grid-editor" data-theme={theme || 'bold'}>
      {/* Controls bar */}
      <div className="gallery-grid-controls">
        <div className="gallery-grid-control-group">
          <label>Columns</label>
          <div className="gallery-grid-col-buttons">
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                className={columns === n ? 'active' : ''}
                onClick={() => setColumnCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        <div className="gallery-grid-control-group">
          <label>Row Height</label>
          <input
            type="range"
            min="150"
            max="500"
            value={rowHeight}
            onChange={(e) => setRowHeight(parseInt(e.target.value))}
          />
          <span className="gallery-grid-value">{rowHeight}px</span>
        </div>
        <div className="gallery-grid-control-group">
          <label>Gap</label>
          <input
            type="range"
            min="0"
            max="24"
            value={gap}
            onChange={(e) => setGap(parseInt(e.target.value))}
          />
          <span className="gallery-grid-value">{gap}px</span>
        </div>
        <button className="gallery-grid-reset" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* Grid area — matches template gallery rendering */}
      <div
        className={`gallery-grid-area${resizingHandle !== null ? ' resizing' : ''}`}
        ref={gridRef}
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplateColumns,
          gridAutoRows: `${rowHeight}px`,
          gap: `${gap}px`,
        }}
      >
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className={`gallery-grid-photo${dragSourceIndex === index ? ' dragging' : ''}${dragOverIndex === index ? ' drag-over' : ''}`}
            style={{ borderRadius: `${ts.borderRadius}px` }}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <img
              src={getMediaUrl(photo.url)}
              alt={photo.title || 'Gallery photo'}
              style={{
                objectPosition: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
                transform:
                  (photo.zoom ?? 100) > 100 ? `scale(${(photo.zoom ?? 100) / 100})` : undefined,
                transformOrigin: `${photo.position_x ?? 50}% ${photo.position_y ?? 50}%`,
              }}
            />
            {/* Title overlay — matches template pattern */}
            {ts.hasOverlay && photo.title && (
              <div
                className={`gallery-grid-photo-overlay gallery-grid-photo-overlay-${ts.overlayStyle}`}
              >
                <span>{photo.title}</span>
              </div>
            )}
            {/* Drag hint + crop */}
            <div className="gallery-grid-photo-actions">
              {onCropPhoto && (
                <button
                  className="gallery-grid-crop-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCropPhoto(photo, e);
                  }}
                  title="Crop"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                    <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                  </svg>
                </button>
              )}
              <div className="gallery-grid-drag-hint">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="5 9 2 12 5 15" />
                  <polyline points="9 5 12 2 15 5" />
                  <polyline points="15 19 12 22 9 19" />
                  <polyline points="19 9 22 12 19 15" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <line x1="12" y1="2" x2="12" y2="22" />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Drag handles overlaid between columns */}
      {columns > 1 && (
        <div
          className="gallery-grid-handles"
          style={{
            position: 'relative',
            marginTop: `-${(gridRef.current?.offsetHeight || 0) + 8}px`,
            height: `${gridRef.current?.offsetHeight || 0}px`,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: gridTemplateColumns,
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {columnWidths.map((_, i) => (
              <div key={i} style={{ position: 'relative' }}>
                {i < columns - 1 && (
                  <div
                    className={`gallery-grid-handle${resizingHandle === i ? ' active' : ''}`}
                    onMouseDown={(e) => handleResizeStart(e, i)}
                    style={{ pointerEvents: 'auto' }}
                  >
                    <div className="gallery-grid-handle-line" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { getDefaultConfig };
export default GalleryGridEditor;
